import * as z from 'zod';

import { assertNever } from '../assertNever';
import {
  AuthorizedContactId,
  BorrowerId,
  DelinquentInterestSettingId,
  EntityId,
  EscrowPaymentHistoryItemId,
  InterestOnEscrowDepositHistoryItemId,
  LoanId,
  PaymentConfigId,
  PaymentId,
  PayoffId,
  TransferId,
} from '../BrandedIds';
import { toJSInstantDate, zodJSInstantDate, zodJSMixedDate } from '../dates/BrandedDate';
import { jsDateTimeToTemporalPlainDate, jsPlainDateToTemporalPlainDate, Temporal } from '../Temporal';
import { LoanStatusActive, LoanStatusPendingTransfer, LoanStatusPreActive, LoanStatusTransferred } from './LoanStatus';

export const PaymentStatus = z.enum(['PENDING', 'PROCESSED', 'FAILED', 'CANCELLED']);
export type PaymentStatus = z.infer<typeof PaymentStatus>;

/**
 * `'principalPrepayment'` is **deprecated** for new payments. It conflates a
 * payment method (how money arrived) with a payment allocation (it was a
 * curtailment), which are orthogonal concepts. New curtailment payments
 * record a real method (check / wire / ACH / ...) and rely on the
 * `principalPrepayment` *breakdown* type to identify the curtailment.
 *
 * The value is retained on this enum so legacy snapshots continue to parse
 * and so the system-synthetic construction-to-permanent path can keep using
 * it as a documented exception. Do not introduce new producers.
 *
 * Use `isPrincipalCurtailmentPayment` (in `PaymentBreakdown.ts`) to ask
 * "is this a curtailment?" — it handles both legacy and new shapes.
 */
export const PaymentMethod = z.enum([
  'check',
  'wire',
  'phone',
  'creditCard',
  'debitCard',
  'dwolla',
  'reversal',
  'reserve',
  'reserveReversal',
  'balanceForward',
  'principalPrepayment', // @deprecated — see comment above
  'authorizedACH',
  'repayACH',
  'nachaACH',
  'balanceReallocation',
  'lockbox',
]);
export type PaymentMethod = z.infer<typeof PaymentMethod>;

export const NonAchPaymentMethods = z.enum([
  PaymentMethod.enum.check,
  PaymentMethod.enum.wire,
  PaymentMethod.enum.phone,
  PaymentMethod.enum.creditCard,
  PaymentMethod.enum.debitCard,
  PaymentMethod.enum.reserve,
  PaymentMethod.enum.principalPrepayment,
  PaymentMethod.enum.lockbox,
]);
export type NonAchPaymentMethods = z.infer<typeof NonAchPaymentMethods>;

export const AchPaymentMethods = z.enum([
  PaymentMethod.enum.dwolla,
  PaymentMethod.enum.authorizedACH,
  PaymentMethod.enum.repayACH,
  PaymentMethod.enum.nachaACH,
]);
export type AchPaymentMethods = z.infer<typeof AchPaymentMethods>;

/**
 * How a payment date field is constructed by method:
 *   - `instant`: derived from `new Date()` (system/ACH flows) — has time-of-day, must be
 *     resolved in the company's operating timezone to land on the right calendar day.
 *   - `plainDate`: user-supplied calendar day stored at UTC midnight — already a calendar day,
 *     pass through as-is.
 *   - `mixed`: construction sites vary (e.g. handler path is plain, system fallback is `new Date()`).
 *     Resolve at value level: UTC midnight is treated as a plain date, otherwise as an instant.
 */
export type PaymentDateKind = 'instant' | 'plainDate' | 'mixed';

export const PAYMENT_METHOD_DATE_KIND: Record<PaymentMethod, PaymentDateKind> = {
  dwolla: 'instant',
  authorizedACH: 'instant',
  repayACH: 'instant',
  nachaACH: 'instant',
  balanceForward: 'instant',
  check: 'plainDate',
  wire: 'plainDate',
  phone: 'plainDate',
  creditCard: 'plainDate',
  debitCard: 'plainDate',
  lockbox: 'plainDate',
  principalPrepayment: 'plainDate',
  // `reserve` is plain in cron / GraphQL / CTP paths, but PaymentBreakdownCalculator inherits the
  // parent payment's kind (e.g. instant-flavored when the parent is dwolla/ACH).
  reserve: 'mixed',
  // `balanceReallocation` is plain via the handler path, instant via fixBreakdowns system fixup.
  balanceReallocation: 'mixed',
  // handleReversePayment defaults both paymentDate and processedDate to `now` (instant), but
  // callers can override paymentDate with a plain date.
  reversal: 'mixed',
  reserveReversal: 'mixed',
};

/**
 * `processedDate` is set when a payment lands in a terminal status. The construction varies by
 * method: dwolla/usio/repay use `new Date()` at handler-invocation time (instants); nachaACH
 * copies the batch's `batchDate` (normalized to UTC midnight, so a plain date); manual entries
 * use a user-selected calendar day. `balanceForward` and `balanceReallocation` never set this
 * field — the kind is moot.
 */
export const PAYMENT_METHOD_PROCESSED_DATE_KIND: Record<PaymentMethod, PaymentDateKind> = {
  dwolla: 'instant',
  authorizedACH: 'instant',
  repayACH: 'instant',
  // NACHA's processedAt is set to `batch.batchDate` (UTC midnight via `.startOf('day')`) when the
  // batch is submitted; the cron just copies that value to Payment.processedDate.
  nachaACH: 'plainDate',
  check: 'plainDate',
  wire: 'plainDate',
  phone: 'plainDate',
  creditCard: 'plainDate',
  debitCard: 'plainDate',
  lockbox: 'plainDate',
  principalPrepayment: 'plainDate',
  // No construction site sets processedDate on these; default kind is moot but pick the safer side.
  balanceForward: 'plainDate',
  balanceReallocation: 'plainDate',
  // reserve auto-payments get processedDate from updateAutomaticReservePayments (`now`, instant);
  // CTP-driven reserve sets `modifiedEffectiveDate` (plain).
  reserve: 'mixed',
  // handleReversePayment defaults processedDate to `now`; GraphQL caller can override with plain.
  reversal: 'mixed',
  reserveReversal: 'mixed',
};

const isDateAtUTCMidnight = (d: Date): boolean =>
  d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0;

const resolveToPlainDate = (d: Date, kind: PaymentDateKind, timeZone: Temporal.TimeZoneLike): Temporal.PlainDate => {
  switch (kind) {
    case 'plainDate':
      return jsPlainDateToTemporalPlainDate(d);
    case 'instant':
      return jsDateTimeToTemporalPlainDate(d, timeZone);
    case 'mixed':
      return isDateAtUTCMidnight(d) ? jsPlainDateToTemporalPlainDate(d) : jsDateTimeToTemporalPlainDate(d, timeZone);
    default:
      return assertNever(kind);
  }
};

/**
 * Resolves a {@link Payment}'s `paymentDate` to a {@link Temporal.PlainDate} based on how the
 * field is constructed for that {@link PaymentMethod}.
 */
export const paymentDateToPlainDate = (
  payment: Pick<Payment, 'method' | 'paymentDate'>,
  timeZone: Temporal.TimeZoneLike,
): Temporal.PlainDate => resolveToPlainDate(payment.paymentDate, PAYMENT_METHOD_DATE_KIND[payment.method], timeZone);

/**
 * Resolves a {@link Payment}'s optional `processedDate` to a {@link Temporal.PlainDate} based on
 * how the field is constructed for that {@link PaymentMethod}. Returns `undefined` when unset.
 */
export const paymentProcessedDateToPlainDate = (
  payment: Pick<Payment, 'method' | 'processedDate'>,
  timeZone: Temporal.TimeZoneLike,
): Temporal.PlainDate | undefined =>
  payment.processedDate == null
    ? undefined
    : resolveToPlainDate(payment.processedDate, PAYMENT_METHOD_PROCESSED_DATE_KIND[payment.method], timeZone);

/**
 * Dwolla transfer
 */
const DwollaBorrowerTransferType = z.literal('balance');

// These are deprecated except for single, need to keep around for old transfers.
// `fundedTransferRecovery` marks a balance -> bank transfer the recovery cron initiates when Dwolla's
// auto-created funded transfer never moved the funds out of the company's Dwolla balance.
export const DwollaLenderTransferType = z.enum([
  'principal',
  'escrow',
  'suspense',
  'suspenseRemaining',
  'single',
  'fundedTransferRecovery',
]);
export type DwollaLenderTransferType = z.infer<typeof DwollaLenderTransferType>;

export const DwollaTransferType = z.union([DwollaBorrowerTransferType, DwollaLenderTransferType]);
export type DwollaTransferType = z.infer<typeof DwollaTransferType>;

export const DwollaSweepType = z.enum(['sweepToBalance', 'sweepToAccount']);
export type DwollaSweepType = z.infer<typeof DwollaSweepType>;

export type MassTransfer = Partial<Record<'single', number>>;

export const DwollaBaseTransfer = z.object({
  id: TransferId,
  amount: z.number(),
  transferUrl: z.string(),
  status: PaymentStatus,
  paymentDate: zodJSInstantDate,
  type: DwollaTransferType,
});
export type DwollaBaseTransfer = z.infer<typeof DwollaBaseTransfer>;

// Dwolla transfer
export const Transfer = z.object({
  status: PaymentStatus,
  processedDate: zodJSInstantDate.optional(),
  toBalance: z.array(DwollaBaseTransfer),
  toAccount: z.array(DwollaBaseTransfer),
  clawbacks: z.array(DwollaBaseTransfer).default([]),
  failureCode: z.string().optional(),
});
export type Transfer = z.infer<typeof Transfer>;

/*
 * Usio Transfer
 */
export const UsioActiveTransfer = z.object({
  status: z.enum(['PENDING', 'PROCESSED', 'CANCELLED']),
  accountToken: z.string().optional(),
  batchedDate: zodJSInstantDate.optional(),
  batch: z.string().optional(),
  processedDate: zodJSInstantDate.optional(),
});
export const UsioFailedTransfer = z.object({
  status: z.literal('FAILED'),
  accountToken: z.string().optional(),
  batchedDate: zodJSInstantDate.optional(),
  batch: z.string().optional(),
  failureCode: z.string(),
  processedDate: zodJSInstantDate.optional(),
});
export type UsioFailedTransfer = z.infer<typeof UsioFailedTransfer>;
export type UsioActiveTransfer = z.infer<typeof UsioActiveTransfer>;
export const UsioTransfer = z.union([UsioActiveTransfer, UsioFailedTransfer]);
export type UsioTransfer = z.infer<typeof UsioTransfer>;

/*
 * Repay transfer
 */
export const RepayActiveTransfer = z.object({
  status: z.enum(['PENDING', 'PROCESSED', 'CANCELLED']),
  trackingNumber: z.string(),
  accountHolderId: z.string(),
  batch: z.string().optional(),
  processedDate: zodJSInstantDate.optional(),
  payAccountId: z.string().optional(), // Used to lock account if transfer fails for non R01 reasons
});
export type RepayActiveTransfer = z.infer<typeof RepayActiveTransfer>;
export const RepayFailedTransfer = z.object({
  status: z.literal('FAILED'),
  trackingNumber: z.string(),
  accountHolderId: z.string(),
  batch: z.string().optional(),
  payAccountId: z.string().optional(), // Used to lock account if transfer fails for non R01 reasons
  failureCode: z.string().optional(),
  processedDate: zodJSInstantDate.optional(),
});
export type RepayFailedTransfer = z.infer<typeof RepayFailedTransfer>;
export const RepayTransfer = z.union([RepayActiveTransfer, RepayFailedTransfer]);
export type RepayTransfer = z.infer<typeof RepayTransfer>;

export const BorrowerPaymentPayer = z.object({
  id: BorrowerId,
  type: z.literal('borrower'),
});
export type BorrowerPaymentPayer = z.infer<typeof BorrowerPaymentPayer>;

export const AuthorizedContactPaymentPayer = z.object({
  id: AuthorizedContactId,
  type: z.literal('authorizedContact'),
});
export type AuthorizedContactPaymentPayer = z.infer<typeof AuthorizedContactPaymentPayer>;

export const EntityPaymentPayer = z.object({
  id: EntityId,
  type: z.literal('entity'),
});
export type EntityPaymentPayer = z.infer<typeof EntityPaymentPayer>;

export const ThirdPartyPaymentPayer = z.object({
  name: z.string(),
  type: z.literal('thirdParty'),
});
export type ThirdPartyPaymentPayer = z.infer<typeof ThirdPartyPaymentPayer>;

export const PaymentPayer = z.discriminatedUnion('type', [
  BorrowerPaymentPayer,
  EntityPaymentPayer,
  AuthorizedContactPaymentPayer,
  ThirdPartyPaymentPayer,
]);
export type PaymentPayer = z.infer<typeof PaymentPayer>;

const BalanceDebitSource = z.object({
  type: z.literal('balance'),
  balance: z.enum(['unappliedSuspense', 'futureStatementSuspense']),
  amount: z.number(),
});
export type BalanceDebitSource = z.infer<typeof BalanceDebitSource>;
const ExternalDebitSource = z.object({
  type: z.literal('external'),
  amount: z.number(),
});
export type ExternalDebitSource = z.infer<typeof ExternalDebitSource>;
export const PaymentDebitSource = z.discriminatedUnion('type', [BalanceDebitSource, ExternalDebitSource]);
export type PaymentDebitSource = z.infer<typeof PaymentDebitSource>;

export const DEFAULT_CREATED_DATE_PLZ_MIGRATE = toJSInstantDate(new Date('1972-12-14T22:54:37.000Z'));

const PaymentBase = z.object({
  id: PaymentId,
  loanId: LoanId,
  payer: PaymentPayer,
  total: z.number(),
  transactionId: z.string(),
  paymentDate: zodJSMixedDate,
  failureDate: zodJSInstantDate.optional(),
  cancelledDate: zodJSInstantDate.optional(),
  processedDate: zodJSInstantDate.optional(),
  createdDate: zodJSInstantDate.default(DEFAULT_CREATED_DATE_PLZ_MIGRATE), // LESLIE TODO: Make required
  status: PaymentStatus,
  holdback: z.number().optional(),
  lossDraft: z.number().optional(),
  additionalPrincipal: z.number().optional(),
  additionalEscrow: z.number().optional(),
  additionalSuspense: z.number().optional(),
  ignoreReserveCredit: z.boolean().optional(),
  debitSource: PaymentDebitSource.optional(),
  comment: z.string().optional(),
  escrowShortageAnalysisId: EscrowPaymentHistoryItemId.optional(),
  paymentConfigId: PaymentConfigId.optional(), // TODO: Deprecate this
  delinquentInterestSettingId: DelinquentInterestSettingId.optional(),
  isDeposit: z.boolean().optional(),
  payoffId: PayoffId.optional(),
  includeIn1099Int: z.boolean().optional(), // Intended for interest on escrow report but can be repurposed if helpful
  interestOnEscrowDepositHistoryItemId: InterestOnEscrowDepositHistoryItemId.optional(),
});

export const NonAchPayment = PaymentBase.extend({
  method: z.enum([
    PaymentMethod.enum.check,
    PaymentMethod.enum.wire,
    PaymentMethod.enum.phone,
    PaymentMethod.enum.creditCard,
    PaymentMethod.enum.debitCard,
    PaymentMethod.enum.reversal,
    PaymentMethod.enum.reserveReversal,
    PaymentMethod.enum.balanceForward,
    PaymentMethod.enum.principalPrepayment,
    PaymentMethod.enum.balanceReallocation,
    PaymentMethod.enum.lockbox,
  ]),
});
export type NonAchPayment = z.infer<typeof NonAchPayment>;

export const ReservePayment = PaymentBase.extend({
  method: z.literal(PaymentMethod.enum.reserve),
  transfer: Transfer.optional(),
  sweep: Transfer.optional(),
  isAutopayment: z.boolean().optional(),
});
export type ReservePayment = z.infer<typeof ReservePayment>;

export const DwollaPayment = PaymentBase.extend({
  method: z.literal(PaymentMethod.enum.dwolla),
  transfer: Transfer.optional(),
  sweep: Transfer.optional(),
  isAutopayment: z.boolean().optional(),
});
export type DwollaPayment = z.infer<typeof DwollaPayment>;

const SweepEnabledPayment = z.union([DwollaPayment, ReservePayment]);
export type SweepEnabledPayment = z.infer<typeof SweepEnabledPayment>;

export const UsioPayment = PaymentBase.extend({
  method: z.literal(PaymentMethod.enum.authorizedACH),
  isAutopayment: z.boolean().optional(),
  transfer: UsioTransfer.optional(), // TODO: make required
  isSameDayACH: z.boolean().optional(),
});
export type UsioPayment = z.infer<typeof UsioPayment>;

export const RepayPayment = PaymentBase.extend({
  method: z.literal(PaymentMethod.enum.repayACH),
  isAutopayment: z.boolean().optional(),
  transfer: RepayTransfer.optional(), // TODO: make required
});
export type RepayPayment = z.infer<typeof RepayPayment>;

export const NachaPayment = PaymentBase.extend({
  method: z.literal(PaymentMethod.enum.nachaACH),
  isAutopayment: z.boolean().optional(),
  transfer: z
    .object({
      failureCode: z.string(),
    })
    .optional(),
});
export type NachaPayment = z.infer<typeof NachaPayment>;

export const Payment = z.union([NonAchPayment, SweepEnabledPayment, UsioPayment, RepayPayment, NachaPayment]);
export type Payment = z.infer<typeof Payment>;

// DwollaBaseTransfer with added prisma fields
const PaymentTransfer = DwollaBaseTransfer.extend({
  paymentId: PaymentId,
  isSweep: z.boolean(),
});
export type PaymentTransfer = z.infer<typeof PaymentTransfer>;

export const PaymentAllowedStatuses = z.union([
  LoanStatusPreActive,
  LoanStatusActive,
  LoanStatusPendingTransfer,
  LoanStatusTransferred,
]);

export const isAchPayment = (payment: Payment): payment is DwollaPayment | UsioPayment | RepayPayment | NachaPayment =>
  ['dwolla', 'authorizedACH', 'repayACH', 'nachaACH'].includes(payment.method);
export const isNachaPayment = (payment: Payment): payment is NachaPayment => payment.method === 'nachaACH';
export const isRepayPayment = (payment: Payment): payment is RepayPayment => payment.method === 'repayACH';
export const isDwollaPayment = (payment: Payment): payment is DwollaPayment => payment.method === 'dwolla';
export const isDepositPayment = (payment: Payment): boolean => payment.isDeposit === true;
export const isReversalPayment = (payment: Payment): boolean =>
  ['reversal', 'reserveReversal'].includes(payment.method);
/** Reversals share interestOnEscrowDepositHistoryItemId with the deposit they reverse so we also filter by method to exclude reversals. */
export const isInterestOnEscrowDeposit = (payment: Payment): boolean =>
  payment.interestOnEscrowDepositHistoryItemId != null && !isReversalPayment(payment);
